import { type MeResponse, TRANSPORT_DISPLAY_STATUSES, TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT, type TransportDisplayStatus, type TransportProviderTripRow } from "@seagames/contracts";
import { KeysetPager, type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { stepAllocation, transportSummaryOptions } from "../../lib/api/transport";
import { invalidateTrips, providerTripsOptions } from "../../lib/api/transport-fleet";
import { formatClockIn, zoneAbbreviation } from "../../lib/format/datetime";
import { type SearchNavigate, useListPage } from "../../lib/list-page/use-list-page";
import { providerCanStep } from "../../lib/transport/access";
import { deskSummary, driverCount, statusCount } from "../../lib/transport/view";
import { todayIn } from "../../lib/travel/view";
import { toast } from "../../lib/ui/toast";
import { EmptyCard } from "../common/empty-card";
import { PhoneLink, VehicleTypeIcon, useCallingCodes } from "../transport-fleet";
import { BagIcon, PhoneIcon, RouteIcon, WheelchairIcon } from "../transport/icons";
import { AllocationStateChip, TripSourceTag, TripStatusChip } from "../transport/status";
import { DayStepper, MetaLine, RouteLine, TimeCell } from "./trip-bits";

/**
 * A transport company's page (TRANSPORT_PROVIDER, PLAN §0b Q1 / §4 "Provider
 * view"): the trips on its vehicles, one day at a time, with Start / Arrived
 * on its own vehicles (its fleet is the layout's Fleet tab). It sees passenger COUNTS
 * per contingent and each group lead's contact, never a name list (the API
 * sends no names to this view). Polled every minute while visible.
 *
 * S4's index route renders it for a session whose view is "provider",
 * spreading `providerTripsSearchShape` and passing `Route.useNavigate()`.
 */

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
const PAGE_SIZES = [25, 50, 100] as const;

export const providerTripsSearchShape = {
	date: lenient(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
	limit: lenient(z.coerce.number().int().min(1).max(100)),
	cursor: lenient(z.string().min(1).max(200)),
	prev: lenient(z.array(z.string().min(1).max(200)).max(100)),
};
export type ProviderTripsSearch = z.infer<z.ZodObject<typeof providerTripsSearchShape>>;

export interface ProviderTripsViewProps {
	session: Pick<MeResponse, "active_role" | "group">;
	gamesId: string;
	timeZone: string;
	search: ProviderTripsSearch;
	navigate: SearchNavigate<ProviderTripsSearch>;
}

/** Its fleet is the layout's "Drivers & vehicles" tab (`FleetView`, read-only for a provider). */
export function ProviderTripsView({ session, gamesId, timeZone, search, navigate }: ProviderTripsViewProps) {
	// `/auth/me` names the company (S2); an older API leaves `facilities` out.
	const company = (session.group?.facilities ?? []).map((f) => f.transport_provider?.name).filter(Boolean).join(", ");
	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ProviderDay gamesId={gamesId} timeZone={timeZone} search={search} navigate={navigate} company={company} canStep={providerCanStep(session)} />
		</div>
	);
}

function ProviderDay({ gamesId, timeZone, search, navigate, company, canStep }: Omit<ProviderTripsViewProps, "session"> & { company: string; canStep: boolean }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const today = todayIn(timeZone);
	const date = search.date ?? today;
	const summary = useQuery(transportSummaryOptions(gamesId, date));
	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => providerTripsOptions({ games_id: gamesId, tab: "day", date, cursor: q.cursor, limit: q.limit }),
		columnsKey: "transport-provider-trips",
		pageSizes: PAGE_SIZES,
		defaultPageSize: TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT,
		summary: { showing: (v) => t("trips.showing", v), showingNoTotal: (v) => t("trips.showingNoTotal", v), empty: t("trips.showingEmpty") },
	});

	// A provider gets the full summary shape, scoped in SQL to its own vehicles and drivers.
	const s = deskSummary(summary.data);
	const n = (k: TransportDisplayStatus) => (s ? statusCount(s, k) : 0);
	const cells: StatsCell[] = [
		{ key: "trips", label: t("provider.strip.trips"), value: s ? TRANSPORT_DISPLAY_STATUSES.reduce((a, k) => a + n(k), 0) : "—" },
		{ key: "waiting", label: t("provider.strip.waiting"), value: s ? n("ASSIGNED") + n("SHORT") : "—" },
		{ key: "road", label: t("provider.strip.onTheRoad"), value: s ? s.on_the_road.trips : "—", note: s ? t("provider.strip.drivers", { count: s.on_the_road.drivers, n: formatNumber(s.on_the_road.drivers) }) : undefined },
		{ key: "done", label: t("provider.strip.arrived"), value: s ? n("COMPLETED") + n("PARTIAL") : "—" },
		{ key: "duty", label: t("provider.strip.onDuty"), value: s ? driverCount(s, "ON_DUTY") + driverCount(s, "ON_TRIP") : "—", of: s ? t("provider.strip.ofDrivers", { n: formatNumber(s.drivers.total) }) : undefined },
	];

	return (
		<>
			<div className="flex flex-wrap items-center justify-between gap-[0.75rem]">
				<DayStepper date={date} today={today} onDate={(d) => void navigate({ search: (p) => ({ ...p, date: d === today ? undefined : d, cursor: undefined, prev: undefined }), replace: true })} />
				<p className="text-[0.75rem] text-gms-ink-3">
					{company ? `${t("provider.company", { name: company })} · ` : ""}
					{t("provider.zoneNote", { zone: zoneAbbreviation(timeZone) })}
				</p>
			</div>
			<StatsStrip label={t("provider.strip.label")} cells={cells} className={summary.isFetching && s ? "opacity-60 transition-opacity" : "transition-opacity"} />

			{page.list.isError ? (
				<EmptyCard
					tone="bad"
					title={t("trips.loadFailed")}
					actions={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void page.list.refetch()}>
							{t("common.retry")}
						</button>
					}
				>
					{page.table.error?.message}
				</EmptyCard>
			) : page.list.isPending ? (
				<div className="grid gap-[0.625rem]" aria-busy>
					<div className="skeleton h-[7rem] rounded-[0.625rem]" />
					<div className="skeleton h-[7rem] rounded-[0.625rem]" />
				</div>
			) : page.rows.length === 0 ? (
				<EmptyCard icon={<RouteIcon />} title={t("provider.emptyTitle")}>
					{t("provider.emptyDescription")}
				</EmptyCard>
			) : (
				<>
					<ul className={`grid gap-[0.625rem] ${page.list.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}`}>
						{page.rows.map((r) => (
							<li key={r.transport_request_id}>
								<ProviderTripCard trip={r} timeZone={timeZone} canStep={canStep} />
							</li>
						))}
					</ul>
					<KeysetPager {...page.pager} position="bottom" />
				</>
			)}
		</>
	);
}

/** One trip on the company's vehicles: counts and leads per contingent, then its own vehicles with Start / Arrived. */
function ProviderTripCard({ trip, timeZone, canStep }: { trip: TransportProviderTripRow; timeZone: string; canStep: boolean }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const callingCodes = useCallingCodes();
	const [busy, setBusy] = useState<string | null>(null);
	const closed = trip.state !== "OPEN";
	// The people on the trip now (its contingents' counts) against the seats its own vehicles were assigned.
	const people = trip.groups.length > 0 ? trip.groups.reduce((n, g) => n + g.passengers, 0) : trip.passengers;
	const assigned = trip.allocations.filter((a) => a.state !== "CANCELLED").reduce((n, a) => n + a.pax, 0);
	const fewerNow = !closed && assigned > people ? people : null;

	const step = async (allocationId: string, plate: string, kind: "start" | "arrive") => {
		setBusy(allocationId);
		try {
			await stepAllocation(allocationId, kind);
			await invalidateTrips();
			toast.success(kind === "start" ? t("provider.started", { plate }) : t("provider.arrived", { plate }));
		} catch (err) {
			toast.error(err, kind === "start" ? t("provider.startFailed") : t("provider.arriveFailed"));
		} finally {
			setBusy(null);
		}
	};

	return (
		<article className={`grid gap-x-[1rem] gap-y-[0.625rem] rounded-box border border-gms-line bg-base-100 px-[1rem] py-[0.75rem] sm:grid-cols-[5.5rem_minmax(0,1fr)_auto] ${closed ? "bg-gms-soft/60" : ""}`}>
			<TimeCell at={trip.pickup_at} timeZone={timeZone} muted={closed} />
			<div className="grid min-w-0 gap-[0.25rem]">
				<RouteLine from={trip.from.name} to={trip.to.name} />
				<MetaLine>
					{[
						<span key="ref" className="font-medium text-gms-ink-2">{trip.ref}</span>,
						<span key="pax">{t("trips.passengers", { count: trip.passengers, n: formatNumber(trip.passengers) })}</span>,
						trip.wheelchair > 0 ? (
							<span key="wc" className="inline-flex items-center gap-[0.25rem]" title={t("trips.wheelchairTitle")}>
								<WheelchairIcon className="size-[0.8125rem]" />
								{formatNumber(trip.wheelchair)}
							</span>
						) : null,
						trip.bags > 0 ? (
							<span key="bags" className="inline-flex items-center gap-[0.25rem]" title={t("trips.bagsTitle")}>
								<BagIcon className="size-[0.8125rem]" />
								{formatNumber(trip.bags)}
							</span>
						) : null,
						trip.return_pickup_at ? <span key="ret">{t("trips.return", { time: formatClockIn(trip.return_pickup_at, timeZone) })}</span> : null,
						<TripSourceTag key="src" source={trip.source} />,
					]}
				</MetaLine>

				{/* Contingents on board: a count and whom to call — never names (Q1). */}
				{trip.groups.length > 0 && (
					<ul className="grid gap-[0.125rem]">
						{trip.groups.map((g) => (
							<li key={`${g.organization_code ?? "host"}-${g.organization_name ?? ""}`} className="flex flex-wrap items-center gap-x-[0.375rem] text-[0.78125rem] text-gms-ink-2">
								<b className="font-semibold text-gms-navy-ink">{g.organization_code ?? g.organization_name ?? t("provider.hostGroup")}</b>
								<span>{t("trips.passengers", { count: g.passengers, n: formatNumber(g.passengers) })}</span>
								{g.wheelchair > 0 && (
									<span className="inline-flex items-center gap-[0.25rem]">
										<WheelchairIcon className="size-[0.75rem]" />
										{formatNumber(g.wheelchair)}
									</span>
								)}
								{g.lead_contact && (g.lead_contact.name || g.lead_contact.phone) && (
									<>
										<span aria-hidden>·</span>
										<span>{t("provider.lead", { name: g.lead_contact.name ?? "—" })}</span>
										{g.lead_contact.phone && (
											<span className="inline-flex items-center gap-[0.25rem]">
												<PhoneIcon className="size-[0.75rem] text-gms-ink-3" />
												<PhoneLink phone={g.lead_contact.phone} callingCodes={callingCodes} />
											</span>
										)}
									</>
								)}
							</li>
						))}
					</ul>
				)}
				{trip.remarks && <p className="text-[0.78125rem] text-gms-ink-3">{trip.remarks}</p>}
				{trip.state_reason && closed && <span className="rounded-[0.375rem] bg-gms-warn-soft px-[0.5rem] py-[0.25rem] text-[0.78125rem] text-gms-warn">{t("trips.reason", { reason: trip.state_reason })}</span>}

				{/* Fewer people now than its vehicles hold (a rebooking moved some off): say so, quietly (E2E N2). */}
				{fewerNow !== null && <p className="text-[0.78125rem] text-gms-ink-3">{t("provider.fewerNow", { count: fewerNow, n: formatNumber(fewerNow) })}</p>}

				{/* Its own vehicles on this trip. */}
				<ul className="mt-[0.25rem] grid gap-[0.375rem] border-t border-gms-line pt-[0.5rem]">
					{trip.allocations.map((a) => (
						<li key={a.transport_allocation_id} className="flex flex-wrap items-center gap-x-[0.5rem] gap-y-[0.375rem] text-[0.8125rem]">
							<VehicleTypeIcon type={a.vehicle.vehicle_type} className="size-[1rem] text-gms-ink-3" />
							<b className="font-semibold tracking-[0.02em] text-gms-navy-ink">{a.vehicle.plate}</b>
							<span className="text-gms-ink-2">{a.driver.name}</span>
							<PhoneLink phone={a.driver.phone} callingCodes={callingCodes} className="text-[0.78125rem]" />
							<span className="text-gms-ink-3 tabular-nums">{t("provider.pax", { count: a.pax, n: formatNumber(a.pax), seats: formatNumber(a.vehicle.seats) })}</span>
							<AllocationStateChip state={a.state} />
							<span className="ml-auto flex gap-[0.375rem]">
								{canStep && a.can_start && (
									<button type="button" className="btn btn-primary btn-sm" disabled={busy !== null} onClick={() => void step(a.transport_allocation_id, a.vehicle.plate, "start")}>
										{busy === a.transport_allocation_id && <span className="loading loading-spinner loading-xs" />}
										{t("provider.start")}
									</button>
								)}
								{canStep && a.can_arrive && (
									<button type="button" className="btn btn-primary btn-sm" disabled={busy !== null} onClick={() => void step(a.transport_allocation_id, a.vehicle.plate, "arrive")}>
										{busy === a.transport_allocation_id && <span className="loading loading-spinner loading-xs" />}
										{t("provider.arrive")}
									</button>
								)}
							</span>
						</li>
					))}
				</ul>
			</div>
			<div className="sm:col-start-3 sm:row-start-1">
				<TripStatusChip status={trip.status} />
			</div>
		</article>
	);
}
